import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import dayjs from 'dayjs';

// Debounce utility for date updates
let dateUpdateTimeout;
const debounceDateUpdate = (callback, delay = 50) => {
  clearTimeout(dateUpdateTimeout);
  dateUpdateTimeout = setTimeout(callback, delay);
};

// Helper function to ensure dates are dayjs objects with caching
const dateCache = new Map();

const ensureDayjs = (date) => {
  if (!date) return dayjs().startOf('month');
  
  // Check cache first for performance
  let cacheKey;
  if (typeof date === 'string') {
    cacheKey = date;
  } else if (dayjs.isDayjs(date)) {
    cacheKey = date.format();
  } else if (date instanceof Date) {
    cacheKey = date.toISOString();
  } else {
    cacheKey = String(date);
  }
  
  if (dateCache.has(cacheKey)) {
    return dateCache.get(cacheKey);
  }
  
  let result;
  if (dayjs.isDayjs(date)) {
    result = date.clone();
  } else if (typeof date === 'string') {
    const parsed = dayjs(date);
    result = parsed.isValid() ? parsed : dayjs().startOf('month');
  } else if (date instanceof Date) {
    result = dayjs(date);
  } else {
    result = dayjs().startOf('month');
  }
  
  // Cache the result (limit cache size to prevent memory leaks)
  if (dateCache.size > 100) {
    dateCache.clear();
  }
  dateCache.set(cacheKey, result);
  
  return result;
};

const useFilterStore = create(
  persist(
    (set, get) => ({
      // Global filters (apply to all views)
      startDate: dayjs().startOf('month'),
      endDate: dayjs().endOf('month'),
      setStartDate: (date) => {
        const dayjsDate = ensureDayjs(date);
        // Only update if the date is actually different
        const currentState = get();
        if (!dayjsDate.isSame(currentState.startDate)) {
          set({ startDate: dayjsDate });
        }
      },
      setEndDate: (date) => {
        const dayjsDate = ensureDayjs(date);
        // Only update if the date is actually different
        const currentState = get();
        if (!dayjsDate.isSame(currentState.endDate)) {
          set({ endDate: dayjsDate });
        }
      },
      // Debounced setters for rapid date changes (e.g., dragging date picker)
      setStartDateDebounced: (date) => {
        debounceDateUpdate(() => {
          const dayjsDate = ensureDayjs(date);
          const currentState = get();
          if (!dayjsDate.isSame(currentState.startDate)) {
            set({ startDate: dayjsDate });
          }
        });
      },
      setEndDateDebounced: (date) => {
        debounceDateUpdate(() => {
          const dayjsDate = ensureDayjs(date);
          const currentState = get();
          if (!dayjsDate.isSame(currentState.endDate)) {
            set({ endDate: dayjsDate });
          }
        });
      },

      // Generic filters (apply across cost/resource views)
      filters: [],
      setFilters: (filtersOrUpdater) => {
        // Support both direct value and functional updates
        if (typeof filtersOrUpdater === 'function') {
          set((state) => ({
            filters: filtersOrUpdater(state.filters)
          }));
        } else {
          set({ filters: filtersOrUpdater });
        }
      },
      addFilter: (filter) => set((state) => ({
        filters: [...state.filters, filter]
      })),
      removeFilter: (category) => set((state) => ({
        filters: state.filters.filter(f => f.category !== category)
      })),
      updateFilterSelection: (category, selectedValues) => set((state) => ({
        filters: state.filters.map(f => 
          f.category === category 
            ? { ...f, selectedValues } 
            : f
        )
      })),

      // View-specific filters (analyzer view only)
      analyzerGrouping: {
        label: "Cost resource type",
        value: "Cost resource type"
      },
      setAnalyzerGrouping: (grouping) => set({ analyzerGrouping: grouping }),

      // Cost analyzer investigation workspace (separate from the legacy
      // analyzerGrouping above, which Forecast.jsx still relies on as a
      // { label, value } string pair).
      analyzerDimension: { id: "total", label: "Total" },
      setAnalyzerDimension: (dimension) => set({ analyzerDimension: dimension }),

      analyzerDrillPath: [],
      setAnalyzerDrillPath: (path) => set({ analyzerDrillPath: path }),

      // Budget mode (BUD3): "" = no budget selected (spend view unchanged);
      // "0" is the whole-project pseudo-budget; anything else is a real
      // budget id, scoping the analyzer to that budget's burn-up chart.
      analyzerBudgetId: "",
      setAnalyzerBudgetId: (id) => set({ analyzerBudgetId: id }),

      analyzerDateGrouping: {
        label: "Day",
        value: "MMM D"
      },
      setAnalyzerDateGrouping: (grouping) => set({ analyzerDateGrouping: grouping }),

      // Report editor settings
      selectedReportType: {
        value: "costs",
        label: "Cost visuals"
      },
      setSelectedReportType: (reportType) => set({ selectedReportType: reportType }),

      // Utility functions
      resetFilters: () => set({
        filters: [],
        analyzerGrouping: {
          label: "Cost resource type",
          value: "Cost resource type"
        },
        analyzerDimension: { id: "total", label: "Total" },
        analyzerDrillPath: [],
        analyzerBudgetId: "",
        analyzerDateGrouping: {
          label: "Day",
          value: "MMM D"
        }
      }),
      clearAllFilters: () => set({
        filters: [],
        startDate: dayjs().startOf('month'),
        endDate: dayjs().endOf('month'),
        analyzerGrouping: {
          label: "Cost resource type",
          value: "Cost resource type"
        },
        analyzerDimension: { id: "total", label: "Total" },
        analyzerDrillPath: [],
        analyzerBudgetId: "",
        analyzerDateGrouping: {
          label: "Day",
          value: "MMM D"
        }
      }),
    }),
    {
      name: 'filter-store',
      partialize: (state) => ({
        startDate: state.startDate,
        endDate: state.endDate,
        filters: state.filters,
        analyzerGrouping: state.analyzerGrouping,
        analyzerDimension: state.analyzerDimension,
        analyzerDrillPath: state.analyzerDrillPath,
        analyzerBudgetId: state.analyzerBudgetId,
        analyzerDateGrouping: state.analyzerDateGrouping,
        selectedReportType: state.selectedReportType,
      }),
      serialize: (state) => {
        return JSON.stringify({
          ...state,
          startDate: state.startDate ? state.startDate.format() : null,
          endDate: state.endDate ? state.endDate.format() : null,
        });
      },
      deserialize: (str) => {
        const parsed = JSON.parse(str);
        return {
          ...parsed,
          startDate: ensureDayjs(parsed.startDate),
          endDate: ensureDayjs(parsed.endDate),
          filters: Array.isArray(parsed.filters) ? parsed.filters : [],
          selectedReportType: parsed.selectedReportType || {
            value: "costs",
            label: "Cost visuals"
          },
        };
      },
    }
  )
);

// Custom hook that ensures dates are dayjs objects and filters is an array
export const useFilterStoreWithDayjsDates = () => {
  // Subscribe to the entire store to avoid multiple subscriptions
  const store = useFilterStore();
  
  // Ensure we get fresh values on every render
  const dayjsStartDate = ensureDayjs(store.startDate);
  const dayjsEndDate = ensureDayjs(store.endDate);
  // Ensure filters is always an array (handles hydration edge cases)
  const safeFilters = Array.isArray(store.filters) ? store.filters : [];
  
  return {
    ...store,
    startDate: dayjsStartDate,
    endDate: dayjsEndDate,
    filters: safeFilters,
  };
};

// Keep the old hook name for backward compatibility
export const useFilterStoreWithMomentDates = useFilterStoreWithDayjsDates;

export default useFilterStore;

// Utility function to reset the store (useful for debugging)
export const resetFilterStore = () => {
  localStorage.removeItem('filter-store');
  // eslint-disable-next-line
  window.location.reload();
};
